import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string): string => i18n.t(`communicationChannels.${key}`);

interface SchemaOptions {
  isFirstSetup: boolean;
  isCurrentlyEnabled: boolean;
}

const requiredString = (messageKey: string) =>
  z
    .string()
    .trim()
    .min(1, { message: t(messageKey) });

const portNumber = () =>
  z.coerce
    .number()
    .int({ message: t("validation.portNumber") })
    .min(1, { message: t("validation.portNumber") });

export const createEmailIntegrationSchema = (opts: SchemaOptions) =>
  z.object({
    enabled: z.boolean(),
    smtp: z
      .object({
        host: requiredString("validation.required"),
        port: portNumber(),
        secure: z.boolean(),
        username: requiredString("validation.required"),
        password: z.string(),
        clearPassword: z.boolean(),
        fromAddress: z
          .string()
          .trim()
          .min(1, { message: t("validation.required") })
          .email({ message: t("validation.email") }),
        fromName: requiredString("validation.required"),
        replyToAddress: z
          .string()
          .trim()
          .min(1, { message: t("validation.required") })
          .email({ message: t("validation.email") }),
      })
      .superRefine((v, ctx) => {
        if (opts.isFirstSetup && !v.password) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.passwordRequiredOnCreate"),
          });
        }
        if (v.clearPassword && opts.isCurrentlyEnabled) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.disableBeforeClearing"),
          });
        }
        if (v.clearPassword && v.password.length > 0) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.clearOrReplaceNotBoth"),
          });
        }
      }),
    imap: z
      .object({
        enabled: z.boolean(),
        host: z.string(),
        port: portNumber(),
        secure: z.boolean(),
        username: z.string(),
        password: z.string(),
        clearPassword: z.boolean(),
        inboxFolder: z.string(),
      })
      .superRefine((v, ctx) => {
        if (!v.enabled) return;
        if (!v.host.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["host"],
            message: t("validation.required"),
          });
        }
        if (!v.username.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["username"],
            message: t("validation.required"),
          });
        }
        if (!v.inboxFolder.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["inboxFolder"],
            message: t("validation.required"),
          });
        }
        if (opts.isFirstSetup && !v.password) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.passwordRequiredOnCreate"),
          });
        }
        if (v.clearPassword && opts.isCurrentlyEnabled) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.disableBeforeClearing"),
          });
        }
        if (v.clearPassword && v.password.length > 0) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: t("validation.clearOrReplaceNotBoth"),
          });
        }
      }),
  });

export const createTwilioIntegrationSchema = (opts: SchemaOptions) =>
  z
    .object({
      enabled: z.boolean(),
      accountSid: requiredString("validation.required"),
      authToken: z.string(),
      clearAuthToken: z.boolean(),
      smsPhoneNumber: z.string(),
      clearSmsPhoneNumber: z.boolean(),
    })
    .superRefine((v, ctx) => {
      if (opts.isFirstSetup && !v.authToken) {
        ctx.addIssue({
          code: "custom",
          path: ["authToken"],
          message: t("validation.authTokenRequiredOnCreate"),
        });
      }
      if (v.clearAuthToken && opts.isCurrentlyEnabled) {
        ctx.addIssue({
          code: "custom",
          path: ["authToken"],
          message: t("validation.disableBeforeClearing"),
        });
      }
      if (v.clearAuthToken && v.authToken.length > 0) {
        ctx.addIssue({
          code: "custom",
          path: ["authToken"],
          message: t("validation.clearOrReplaceNotBoth"),
        });
      }
    });

export const createWhatsappIntegrationSchema = (opts: SchemaOptions) =>
  z
    .object({
      enabled: z.boolean(),
      accessToken: z.string(),
      clearAccessToken: z.boolean(),
      phoneNumberId: requiredString("validation.required"),
      wabaId: requiredString("validation.required"),
      displayPhoneNumber: requiredString("validation.required"),
      appSecret: z.string(),
      clearAppSecret: z.boolean(),
      webhookVerifyToken: z.string(),
      clearWebhookVerifyToken: z.boolean(),
      graphApiVersion: requiredString("validation.required"),
      phoneRegistrationPin: z.string(),
    })
    .superRefine((v, ctx) => {
      const secrets = [
        ["accessToken", "clearAccessToken"],
        ["appSecret", "clearAppSecret"],
        ["webhookVerifyToken", "clearWebhookVerifyToken"],
      ] as const;
      for (const [valueField, clearField] of secrets) {
        if (opts.isFirstSetup && !v[valueField]) {
          ctx.addIssue({
            code: "custom",
            path: [valueField],
            message: t("validation.requiredOnCreate"),
          });
        }
        if (v[clearField] && opts.isCurrentlyEnabled) {
          ctx.addIssue({
            code: "custom",
            path: [valueField],
            message: t("validation.disableBeforeClearing"),
          });
        }
        if (v[clearField] && v[valueField].length > 0) {
          ctx.addIssue({
            code: "custom",
            path: [valueField],
            message: t("validation.clearOrReplaceNotBoth"),
          });
        }
      }
      const pin = v.phoneRegistrationPin.trim();
      if (pin.length > 0 && !/^\d{6}$/.test(pin)) {
        ctx.addIssue({
          code: "custom",
          path: ["phoneRegistrationPin"],
          message: t("validation.whatsappRegistrationPin"),
        });
      }
    });

export type EmailIntegrationFormData = z.input<
  ReturnType<typeof createEmailIntegrationSchema>
>;
export type TwilioIntegrationFormData = z.input<
  ReturnType<typeof createTwilioIntegrationSchema>
>;
export type WhatsappIntegrationFormData = z.input<
  ReturnType<typeof createWhatsappIntegrationSchema>
>;
